import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ADD_COMMENT, DELETE_COMMENT, GET_COMMENTS } from "graphql/comments";
import { useUserMentions } from "context/UserMentionProvider";
import { fullName } from "utils/fullName";
import {
  maskMentions,
  reverseMaskMentions,
  validateComment,
} from "components/common/comments/mentions";
import { Comment, CommentUser } from "components/common/comments/types";

const MAX_MENTIONS = 10;

/**
 * Owns everything related to comments on a parent entity (payment, invoice, etc.):
 *
 * - Fetches the existing comments list for `parentId` (skipped when empty)
 * - Holds the input state for a new comment, including @mention parsing/dropdown
 * - Exposes `submit`, `deleteComment`, and `refetchComments`
 *
 * Pass an empty `parentId` to use the hook in "draft" mode — the list query is
 * skipped, and a caller can later finish submitting via `submit(newParentId)`
 * once the parent entity exists (e.g. after creating a new payment, submit the
 * typed-but-unsent comment against the new payment id).
 */
export const useComments = (parentId: string, parentType: string) => {
  const { t } = useTranslation();
  const { users } = useUserMentions();

  const [rawComment, setRawComment] = useState("");
  const [displayComment, setDisplayComment] = useState("");
  const [mentionDropdownOpen, setMentionDropdownOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [insertedMentions, setInsertedMentions] = useState<Map<string, string>>(
    new Map(),
  );

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const {
    data: commentsData,
    refetch: refetchComments,
    loading: loadingComments,
  } = useQuery(GET_COMMENTS, {
    fetchPolicy: "network-only",
    variables: { parentId },
    skip: !parentId,
  });

  const [addComment, { loading: addingComment }] = useMutation(ADD_COMMENT);

  const [deleteCommentMutation, { loading: deletingComment }] = useMutation(
    DELETE_COMMENT,
    {
      onCompleted: () => {
        if (parentId) refetchComments();
      },
      onError: (error: { message: any }) => {
        toast.error(error?.message || t("Please try again!"));
      },
    },
  );

  const reset = () => {
    // The mention debounce writes the draft back 100ms later, so a timer left
    // running repopulates what this just cleared.
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    setRawComment("");
    setDisplayComment("");
    setInsertedMentions(new Map());
    setMentionDropdownOpen(false);
    setMentionQuery("");
  };

  // A drawer that stays mounted swaps parents under this hook, so an unsent
  // draft would otherwise follow the user from one payment to the next and be
  // savable against it. Only a real parent clears it: the empty id IS the
  // create flow's draft, typed before the entity it belongs to exists, and
  // `submit(newParentId)` posts it afterwards.
  // Tracks the last REAL parent, not the last value: a closing drawer clears
  // the id, and recording that would make reopening the same payment look like
  // a change and discard the draft this deliberately keeps.
  const lastParentId = useRef(parentId);
  useEffect(() => {
    if (!parentId) return;
    if (parentId !== lastParentId.current) reset();
    lastParentId.current = parentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Only run conversion if @ is present (performance optimization)
    const hasAtSymbol = value.includes("@");

    if (hasAtSymbol) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        const raw = reverseMaskMentions(value, insertedMentions);
        setRawComment(raw);
        setDisplayComment(maskMentions(raw, users));
      }, 100);

      const raw = reverseMaskMentions(value, insertedMentions);
      setRawComment(raw);
      setDisplayComment(value);

      setInsertedMentions((prev) => {
        const newMap = new Map<string, string>();
        prev.forEach((displayName, userId) => {
          if (value.includes(`@${displayName}`)) {
            newMap.set(userId, displayName);
          }
        });
        return newMap;
      });
    } else {
      setRawComment(value);
      setDisplayComment(value);
      setInsertedMentions(new Map());
    }

    const match = value.match(/@([\w\s]*)$/);
    if (match) {
      setMentionDropdownOpen(true);
      setMentionQuery(match[1]);
    } else {
      setMentionDropdownOpen(false);
      setMentionQuery("");
    }
  };

  const handleMentionSelect = (user: CommentUser) => {
    const displayName = fullName(user);

    if (insertedMentions.size >= MAX_MENTIONS) {
      toast.warning(t(`Maximum ${MAX_MENTIONS} mentions per comment`));
      setMentionDropdownOpen(false);
      setMentionQuery("");
      return;
    }

    setInsertedMentions((prev) => {
      const newMap = new Map(prev);
      newMap.set(user.id, displayName);
      return newMap;
    });

    setRawComment((prev) => prev.replace(/@[\w\s]*$/, `@[${user.id}] `));
    setDisplayComment((prev) => prev.replace(/@[\w\s]*$/, `@${displayName} `));

    setMentionDropdownOpen(false);
    setMentionQuery("");
  };

  const submit = async (parentIdOverride?: string): Promise<boolean> => {
    const targetId = parentIdOverride ?? parentId;
    if (!targetId) return false;
    if (!rawComment.trim()) return false;

    const validationError = validateComment(rawComment);
    if (validationError) {
      toast.error(validationError);
      return false;
    }

    try {
      await addComment({
        variables: { parentId: targetId, parentType, comment: rawComment },
      });
      reset();
      if (parentId) refetchComments();
      return true;
    } catch (error: any) {
      toast.error(error?.message || t("Please try again!"));
      return false;
    }
  };

  /** Resolves true once deleted, false on a failure, which `onError` toasts. Never rejects. */
  const deleteComment = async (commentId: string): Promise<boolean> => {
    // Apollo 3 resolves with `errors` once `onError` runs; the catch keeps
    // that contract from depending on it.
    try {
      const result = await deleteCommentMutation({ variables: { commentId } });
      return !result.errors;
    } catch {
      return false;
    }
  };

  return {
    isDraft: !parentId,
    comments: commentsData?.comments as Comment[] | undefined,
    loadingComments,
    refetchComments,
    deleteComment,
    deletingComment,

    rawComment,
    displayComment,
    mentionDropdownOpen,
    mentionQuery,
    insertedMentions,
    addingComment,
    hasDraft: rawComment.trim().length > 0,
    handleCommentChange,
    handleMentionSelect,
    setMentionDropdownOpen,
    submit,
    reset,
  };
};

export type CommentActions = ReturnType<typeof useComments>;
